import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import {
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";

import api from "../../../services/api";
import { AuthContext } from "../../../context/Auth/AuthContext";
import { SocketContext } from "../../../context/Socket/SocketContext";
import {
  looksLikeReceitaComprovante,
  parseReceitaCnpj,
} from "../../../utils/receitaCnpjParser";
import { buildReceitaBookmarklet } from "../../../utils/receitaBookmarklet";

const RECEITA_URL =
  "https://solucoes.receita.fazenda.gov.br/servicos/cnpjreva/cnpjreva_solicitacao.asp";

const useStyles = makeStyles((theme) => ({
  iframe: {
    width: "100%",
    height: "600px",
    border: "1px solid #ccc",
    borderRadius: "4px",
  },
  bookmarklet: {
    display: "inline-block",
    padding: theme.spacing(1, 2),
    marginRight: theme.spacing(1),
    border: `1px dashed ${theme.palette.primary.main}`,
    borderRadius: "4px",
    fontWeight: 500,
    cursor: "grab",
  },
}));

const onlyDigits = (value) => (value || "").replace(/\D/g, "");

const ReceitaCnpjImportDialog = ({ open, onClose, expectedCnpj, onImport }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);

  const [showCopyHint, setShowCopyHint] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showPasteBox, setShowPasteBox] = useState(false);
  const [bookmarkletToken, setBookmarkletToken] = useState("");
  const [pendingImport, setPendingImport] = useState(null);

  const iframeRef = useRef(null);
  const pasteInputRef = useRef(null);
  const loadCountRef = useRef(0);
  const interactedRef = useRef(false);
  const lastTextRef = useRef("");

  const applyImport = useCallback(
    (parsed) => {
      onImport(parsed.fields, parsed.meta);
      setPendingImport(null);
      onClose();
    },
    [onImport, onClose]
  );

  // Alimentado por todos os caminhos de importação: clipboard, Ctrl+V,
  // postMessage do bookmarklet e socket.
  const handleText = useCallback(
    (rawText, { silent = false } = {}) => {
      const text = (rawText || "").trim();
      if (!text || text === lastTextRef.current) return;
      lastTextRef.current = text;

      const parsed = parseReceitaCnpj(text);
      if (!parsed) {
        if (!silent) {
          toast.warning(
            "O texto não parece um comprovante de CNPJ da Receita Federal"
          );
        }
        return;
      }

      if (
        parsed.meta.situacaoCadastral &&
        parsed.meta.situacaoCadastral !== "ATIVA"
      ) {
        toast.warning(
          `Atenção: situação cadastral "${parsed.meta.situacaoCadastral}" na Receita Federal`
        );
      }

      const digitado = onlyDigits(expectedCnpj);
      if (parsed.meta.cnpj && digitado && parsed.meta.cnpj !== digitado) {
        setPendingImport(parsed);
        return;
      }

      applyImport(parsed);
    },
    [expectedCnpj, applyImport]
  );

  const handleTextRef = useRef(handleText);
  useEffect(() => {
    handleTextRef.current = handleText;
  }, [handleText]);

  const clipboardLiberado = async () => {
    try {
      const permission = await navigator.permissions.query({
        name: "clipboard-read",
      });
      return permission.state === "granted";
    } catch (error) {
      return false;
    }
  };

  const importarDados = async () => {
    // Só lê a área de transferência com a permissão já concedida: sem ela o
    // readText abre o popup "Colar" do Chrome a cada clique.
    if (await clipboardLiberado()) {
      try {
        const text = await navigator.clipboard.readText();
        if (text) {
          handleText(text);
          return;
        }
      } catch (error) {
        // cai no campo de colagem abaixo
      }
    }

    setShowPasteBox(true);
  };

  // Token do bookmarklet (por usuário) — sem ele o bloco avançado fica oculto.
  useEffect(() => {
    if (!open) return;

    let ativo = true;
    (async () => {
      try {
        const { data } = await api.get("/clientes/cnpj/import-token");
        if (ativo) setBookmarkletToken(data.token || "");
      } catch (error) {
        if (ativo) setBookmarkletToken("");
      }
    })();

    return () => {
      ativo = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      loadCountRef.current = 0;
      interactedRef.current = false;
      lastTextRef.current = "";
      setShowCopyHint(false);
      setShowPasteBox(false);
      setPendingImport(null);
      return undefined;
    }

    // Clicar dentro do iframe tira o foco da nossa janela: é o sinal de que o
    // usuário está mexendo na página da Receita.
    const handleBlur = () => {
      if (document.activeElement === iframeRef.current) {
        interactedRef.current = true;
        setShowCopyHint(true);
      }
    };

    const handleFocus = async () => {
      if (!interactedRef.current) return;
      try {
        const permission = await navigator.permissions.query({
          name: "clipboard-read",
        });
        if (permission.state === "granted") {
          const text = await navigator.clipboard.readText();
          if (text && looksLikeReceitaComprovante(text)) {
            handleTextRef.current(text, { silent: true });
            return;
          }
        }
      } catch (error) {
        // Navegador sem a API de permissões: segue para o campo de colagem.
      }

      // Sem leitura automática, o campo de colagem já entra focado para o
      // Ctrl+V funcionar sem nenhum clique e sem o popup "Colar" do Chrome.
      setShowPasteBox(true);
    };

    const handleMessage = (event) => {
      try {
        const { hostname } = new URL(event.origin);
        if (!hostname.endsWith(".fazenda.gov.br")) return;
      } catch (error) {
        return;
      }
      if (event.data?.type !== "receita-cnpj-comprovante") return;
      handleTextRef.current(event.data.text);
    };

    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("message", handleMessage);
    };
  }, [open]);

  // Entrega do bookmarklet que passou pelo backend (funciona em qualquer aba).
  useEffect(() => {
    if (!open || !user?.companyId) return undefined;

    const socket = socketManager.getSocket(user.companyId);
    socket.on(`company-${user.companyId}-cnpj-import`, (data) => {
      handleTextRef.current(data?.text);
    });

    return () => {
      socket.disconnect();
    };
  }, [open, user, socketManager]);

  const handlePaste = (event) => {
    const text = event.clipboardData?.getData("text");
    if (text) handleText(text);
  };

  useEffect(() => {
    if (showPasteBox && pasteInputRef.current) pasteInputRef.current.focus();
  }, [showPasteBox]);

  const handleIframeLoad = () => {
    loadCountRef.current += 1;
    // A segunda carga é a página do comprovante (o iframe é cross-origin e não
    // expõe a URL, então a contagem é a única pista disponível).
    if (loadCountRef.current >= 2) setShowCopyHint(true);
  };

  const abrirEmNovaAba = () => {
    // Sem noopener de propósito: o opener é o que permite o postMessage.
    window.open(RECEITA_URL, "_blank");
    interactedRef.current = true;
    setShowCopyHint(true);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ onPaste: handlePaste }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Consulta Manual CNPJ - Receita Federal
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box mb={2}>
          <Typography variant="body2" color="textSecondary">
            1. Preencha o reCAPTCHA e insira o CNPJ no site da Receita Federal<br />
            2. Clique em "Consultar" para visualizar o comprovante<br />
            3. Selecione a página com Ctrl+A e copie com Ctrl+C<br />
            4. Volte para esta janela: os dados são importados automaticamente
          </Typography>
        </Box>

        {showCopyHint && (
          <Box mb={2}>
            <Alert severity="info">
              Já consultou? Copie a página do comprovante com Ctrl+A e Ctrl+C e
              volte para cá. Se nada acontecer, clique em "Importar dados" ou
              pressione Ctrl+V aqui dentro.
            </Alert>
          </Box>
        )}

        {showPasteBox && (
          <Box mb={2}>
            <TextField
              inputRef={pasteInputRef}
              fullWidth
              multiline
              rows={2}
              variant="outlined"
              size="small"
              label="Cole aqui o comprovante (Ctrl+V)"
              placeholder="Selecione a página do comprovante com Ctrl+A, copie com Ctrl+C e cole aqui com Ctrl+V"
              onChange={(event) => handleText(event.target.value)}
            />
          </Box>
        )}

        {pendingImport && (
          <Box mb={2}>
            <Alert
              severity="warning"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => applyImport(pendingImport)}
                >
                  Importar mesmo assim
                </Button>
              }
            >
              O comprovante é do CNPJ {pendingImport.meta.cnpj}, diferente do
              CNPJ {onlyDigits(expectedCnpj)} preenchido no cadastro.
            </Alert>
          </Box>
        )}

        <iframe
          ref={iframeRef}
          src={RECEITA_URL}
          className={classes.iframe}
          title="Consulta CNPJ - Receita Federal"
          onLoad={handleIframeLoad}
        />

        <Box mt={2}>
          <Link
            component="button"
            variant="body2"
            onClick={() => setShowAdvanced((prev) => !prev)}
          >
            {showAdvanced ? "Ocultar modo avançado" : "Modo avançado (importar com 1 clique)"}
          </Link>
          <Collapse in={showAdvanced}>
            <Box mt={1}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Arraste o botão abaixo para a barra de favoritos do navegador.
                Depois, com o comprovante aberto em uma aba da Receita, clique
                nele: os dados vêm para cá sozinhos, sem precisar copiar.
              </Typography>
              <Box display="flex" alignItems="center" flexWrap="wrap" mt={1}>
                {bookmarkletToken && (
                  <a
                    className={classes.bookmarklet}
                    ref={(el) => {
                      if (el) {
                        el.setAttribute(
                          "href",
                          buildReceitaBookmarklet(bookmarkletToken)
                        );
                      }
                    }}
                    onClick={(event) => event.preventDefault()}
                  >
                    Importar CNPJ
                  </a>
                )}
                <Button
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  onClick={abrirEmNovaAba}
                >
                  Abrir Receita em nova aba
                </Button>
              </Box>
            </Box>
          </Collapse>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={importarDados} color="primary" variant="contained">
          Importar dados
        </Button>
        <Button onClick={onClose} color="primary">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReceitaCnpjImportDialog;
